/* eslint-disable react/only-export-components -- a route table, not a component: an edit here
   reloads the page instead of a hot update, and the pages it loads keep fast refresh */
import { lazy } from 'react';
import { Navigate, Route } from 'react-router';
import { GlobalVehicleModelsRouteGuard } from '@/components/layout/GlobalVehicleModelsRouteGuard';
import { LegacyPolicyRedirect } from '@/components/layout/LegacyPolicyRedirect';
import { PlatformRouteGuard } from '@/components/layout/PlatformRouteGuard';
import { HomeRedirect, RentalRouteGuard } from '@/components/layout/RentalRouteGuard';
import { ROUTES } from '@/constants';
import { RENTAL_PROFILE_SCOPE_MESSAGE } from '@/utils/apiErrorMessage';

// Lazy load all page components for code splitting
const Dashboard = lazy(() => import('@/pages/dashboard').then((m) => ({ default: m.Dashboard })));
const UserList = lazy(() => import('@/pages/users').then((m) => ({ default: m.UserList })));
const UserShow = lazy(() => import('@/pages/users').then((m) => ({ default: m.UserShow })));
const SettingsPage = lazy(() =>
  import('@/pages/settings').then((m) => ({ default: m.SettingsPage })),
);
const RentalProfilePage = lazy(() =>
  import('@/pages/rental-profile').then((m) => ({ default: m.RentalProfilePage })),
);
const UserProfile = lazy(() =>
  import('@/pages/profile/UserProfile').then((m) => ({ default: m.UserProfile })),
);
const QuickStartGuide = lazy(() =>
  import('@/pages/quick-start').then((module) => ({
    default: module.QuickStartGuide,
  })),
);
const NotFound = lazy(() =>
  import('@/pages/errors/NotFound').then((m) => ({ default: m.NotFound })),
);

// Customer pages
const CustomerList = lazy(() =>
  import('@/pages/customers/CustomerList').then((m) => ({ default: m.CustomerList })),
);
const CustomerCreate = lazy(() =>
  import('@/pages/customers/CustomerCreate').then((m) => ({ default: m.CustomerCreate })),
);
const CustomerEdit = lazy(() =>
  import('@/pages/customers/CustomerEdit').then((m) => ({ default: m.CustomerEdit })),
);
const CustomerShow = lazy(() =>
  import('@/pages/customers/CustomerShow').then((m) => ({ default: m.CustomerShow })),
);

// Vehicle Group pages (catalog categories)
const VehicleGroupList = lazy(() =>
  import('@/pages/vehicle-groups/VehicleGroupList').then((m) => ({ default: m.VehicleGroupList })),
);
const VehicleGroupCreate = lazy(() =>
  import('@/pages/vehicle-groups/VehicleGroupCreate').then((m) => ({
    default: m.VehicleGroupCreate,
  })),
);
const VehicleGroupEdit = lazy(() =>
  import('@/pages/vehicle-groups/VehicleGroupEdit').then((m) => ({ default: m.VehicleGroupEdit })),
);
const VehicleGroupShow = lazy(() =>
  import('@/pages/vehicle-groups/VehicleGroupShow').then((m) => ({ default: m.VehicleGroupShow })),
);

const GroupPricing = lazy(() =>
  import('@/pages/group-pricing').then((m) => ({ default: m.GroupPricing })),
);

// Vehicle Model pages (catalog items)
const VehicleModelList = lazy(() =>
  import('@/pages/vehicle-models/VehicleModelList').then((m) => ({ default: m.VehicleModelList })),
);
const VehicleModelCreate = lazy(() =>
  import('@/pages/vehicle-models/VehicleModelCreate').then((m) => ({
    default: m.VehicleModelCreate,
  })),
);
const VehicleModelEdit = lazy(() =>
  import('@/pages/vehicle-models/VehicleModelEdit').then((m) => ({ default: m.VehicleModelEdit })),
);
const VehicleModelShow = lazy(() =>
  import('@/pages/vehicle-models/VehicleModelShow').then((m) => ({ default: m.VehicleModelShow })),
);

// Fleet Vehicle pages (physical instances)
const VehicleList = lazy(() =>
  import('@/pages/vehicles/VehicleList').then((m) => ({ default: m.VehicleList })),
);
const VehicleCreate = lazy(() =>
  import('@/pages/vehicles/VehicleCreate').then((m) => ({ default: m.VehicleCreate })),
);
const VehicleEdit = lazy(() =>
  import('@/pages/vehicles/VehicleEdit').then((m) => ({ default: m.VehicleEdit })),
);
const VehicleShow = lazy(() =>
  import('@/pages/vehicles/VehicleShow').then((m) => ({ default: m.VehicleShow })),
);

// Station pages
const StationList = lazy(() =>
  import('@/pages/stations/StationList').then((m) => ({ default: m.StationList })),
);
const StationCreate = lazy(() =>
  import('@/pages/stations/StationCreate').then((m) => ({ default: m.StationCreate })),
);
const StationEdit = lazy(() =>
  import('@/pages/stations/StationEdit').then((m) => ({ default: m.StationEdit })),
);
const StationShow = lazy(() =>
  import('@/pages/stations/StationShow').then((m) => ({ default: m.StationShow })),
);
const StationFees = lazy(() =>
  import('@/pages/stations/StationFees').then((m) => ({ default: m.StationFees })),
);

// Reservation pages
const ReservationList = lazy(() =>
  import('@/pages/reservations/ReservationList').then((m) => ({ default: m.ReservationList })),
);
const ReservationCreate = lazy(() =>
  import('@/pages/reservations/ReservationCreate').then((m) => ({
    default: m.ReservationCreate,
  })),
);
const ReservationEdit = lazy(() =>
  import('@/pages/reservations/ReservationEdit').then((m) => ({ default: m.ReservationEdit })),
);
const ReservationShow = lazy(() =>
  import('@/pages/reservations/ReservationShow').then((m) => ({ default: m.ReservationShow })),
);

// Extra pages (global add-ons)
const ExtraList = lazy(() =>
  import('@/pages/extras/ExtraList').then((m) => ({ default: m.ExtraList })),
);
const ExtraCreate = lazy(() =>
  import('@/pages/extras/ExtraCreate').then((m) => ({ default: m.ExtraCreate })),
);
const ExtraEdit = lazy(() =>
  import('@/pages/extras/ExtraEdit').then((m) => ({ default: m.ExtraEdit })),
);
const ExtraShow = lazy(() =>
  import('@/pages/extras/ExtraShow').then((m) => ({ default: m.ExtraShow })),
);
const ExtraFees = lazy(() => import('@/pages/extras/fees').then((m) => ({ default: m.ExtraFees })));

// Insurance pages (global insurance options)
const InsuranceList = lazy(() =>
  import('@/pages/insurances/InsuranceList').then((m) => ({ default: m.InsuranceList })),
);
const InsuranceCreate = lazy(() =>
  import('@/pages/insurances/InsuranceCreate').then((m) => ({ default: m.InsuranceCreate })),
);
const InsuranceEdit = lazy(() =>
  import('@/pages/insurances/InsuranceEdit').then((m) => ({ default: m.InsuranceEdit })),
);
const InsuranceShow = lazy(() =>
  import('@/pages/insurances/InsuranceShow').then((m) => ({ default: m.InsuranceShow })),
);
const InsuranceFees = lazy(() =>
  import('@/pages/insurances/fees').then((m) => ({ default: m.InsuranceFees })),
);

// Season pages (recurring yearly date periods)
const SeasonList = lazy(() =>
  import('@/pages/seasons/SeasonList').then((m) => ({ default: m.SeasonList })),
);
const SeasonCreate = lazy(() =>
  import('@/pages/seasons/SeasonCreate').then((m) => ({ default: m.SeasonCreate })),
);
const SeasonEdit = lazy(() =>
  import('@/pages/seasons/SeasonEdit').then((m) => ({ default: m.SeasonEdit })),
);
const SeasonShow = lazy(() =>
  import('@/pages/seasons/SeasonShow').then((m) => ({ default: m.SeasonShow })),
);
const SeasonFees = lazy(() =>
  import('@/pages/seasons/fees').then((m) => ({ default: m.SeasonFees })),
);
const DailyRates = lazy(() =>
  import('@/pages/daily-rates').then((m) => ({ default: m.DailyRates })),
);
const SetupPage = lazy(() => import('@/pages/setup').then((m) => ({ default: m.SetupPage })));
const OfficeHoursPolicies = lazy(() =>
  import('@/pages/other-policies').then((m) => ({ default: m.OfficeHoursPolicies })),
);
const OpeningHoursEditor = lazy(() =>
  import('@/pages/other-policies/office-hours').then((m) => ({
    default: m.OpeningHoursEditor,
  })),
);
const StationOpeningHoursPage = lazy(() =>
  import('@/pages/other-policies/opening-hours/StationOpeningHoursPage').then((m) => ({
    default: m.StationOpeningHoursPage,
  })),
);
const ExtraDriverPolicies = lazy(() =>
  import('@/pages/other-policies').then((m) => ({ default: m.ExtraDriverPolicies })),
);
const DriverAgePolicies = lazy(() =>
  import('@/pages/other-policies').then((m) => ({ default: m.DriverAgePolicies })),
);
const RoadAssistancePolicies = lazy(() =>
  import('@/pages/other-policies').then((m) => ({ default: m.RoadAssistancePolicies })),
);
const KmPolicies = lazy(() =>
  import('@/pages/other-policies').then((m) => ({ default: m.KmPolicies })),
);
const DiscountPolicies = lazy(() =>
  import('@/pages/other-policies').then((m) => ({ default: m.DiscountPolicies })),
);
const RentalDurationPolicies = lazy(() =>
  import('@/pages/other-policies').then((m) => ({ default: m.RentalDurationPolicies })),
);

// Calendar page
const CalendarPage = lazy(() =>
  import('@/pages/calendar').then((m) => ({ default: m.CalendarPage })),
);

// Rental pages (platform-admin only)
const RentalList = lazy(() =>
  import('@/pages/rentals/RentalList').then((m) => ({ default: m.RentalList })),
);
const RentalShow = lazy(() =>
  import('@/pages/rentals/RentalShow').then((m) => ({ default: m.RentalShow })),
);
const SecurityEventList = lazy(() =>
  import('@/pages/security-events/SecurityEventList').then((m) => ({
    default: m.SecurityEventList,
  })),
);

// Global vehicle model pages (platform-admin only, in Global view)
const GlobalVehicleModelList = lazy(() =>
  import('@/pages/global-vehicle-models/GlobalVehicleModelList').then((m) => ({
    default: m.GlobalVehicleModelList,
  })),
);
const GlobalVehicleModelCreate = lazy(() =>
  import('@/pages/global-vehicle-models/GlobalVehicleModelCreate').then((m) => ({
    default: m.GlobalVehicleModelCreate,
  })),
);
const GlobalVehicleModelEdit = lazy(() =>
  import('@/pages/global-vehicle-models/GlobalVehicleModelEdit').then((m) => ({
    default: m.GlobalVehicleModelEdit,
  })),
);
const GlobalVehicleModelShow = lazy(() =>
  import('@/pages/global-vehicle-models/GlobalVehicleModelShow').then((m) => ({
    default: m.GlobalVehicleModelShow,
  })),
);

/**
 * The routes inside the authenticated app layout. A fragment, not a component: <Routes> reads
 * <Route> elements only. App renders it, and the layout tests render it, so they test this tree.
 */
export const protectedRoutes = (
  <>
    <Route index element={<HomeRedirect />} />
    <Route path="/profile" element={<UserProfile />} />

    {/* Routes that need a Rental */}
    <Route element={<RentalRouteGuard />}>
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path={ROUTES.QUICK_START} element={<QuickStartGuide />} />

      {/* Users of the Rental (Manager, or an Admin impersonating) */}
      <Route path={ROUTES.USERS} element={<UserList scope="rental" />} />
      <Route path={ROUTES.USERS_SHOW} element={<UserShow scope="rental" />} />

      {/* Customer routes */}
      <Route path="/customers" element={<CustomerList />} />
      <Route path="/customers/create" element={<CustomerCreate />} />
      <Route path="/customers/edit/:id" element={<CustomerEdit />} />
      <Route path="/customers/show/:id" element={<CustomerShow />} />

      {/* Vehicle Group routes (catalog categories) */}
      <Route path="/vehicle-groups" element={<VehicleGroupList />} />
      <Route path="/vehicle-groups/create" element={<VehicleGroupCreate />} />
      <Route path="/vehicle-groups/edit/:id" element={<VehicleGroupEdit />} />
      <Route path="/vehicle-groups/show/:id" element={<VehicleGroupShow />} />
      <Route path={ROUTES.VEHICLE_GROUPS_PRICING} element={<GroupPricing />} />

      {/* Vehicle Model routes (catalog items) */}
      <Route path="/vehicle-models" element={<VehicleModelList />} />
      <Route path="/vehicle-models/create" element={<VehicleModelCreate />} />
      <Route path="/vehicle-models/edit/:id" element={<VehicleModelEdit />} />
      <Route path="/vehicle-models/show/:id" element={<VehicleModelShow />} />

      {/* Fleet Vehicle routes (physical instances) */}
      <Route path="/vehicles" element={<VehicleList />} />
      <Route path="/vehicles/create" element={<VehicleCreate />} />
      <Route path="/vehicles/edit/:id" element={<VehicleEdit />} />
      <Route path="/vehicles/show/:id" element={<VehicleShow />} />

      {/* Station routes */}
      <Route path="/stations" element={<StationList />} />
      <Route path="/stations/create" element={<StationCreate />} />
      <Route path="/stations/edit/:id" element={<StationEdit />} />
      <Route path="/stations/show/:id" element={<StationShow />} />
      <Route path="/stations/:id/fees" element={<StationFees />} />

      {/* Reservation routes */}
      <Route path="/reservations" element={<ReservationList />} />
      <Route path="/reservations/create" element={<ReservationCreate />} />
      <Route path="/reservations/edit/:id" element={<ReservationEdit />} />
      <Route path="/reservations/show/:id" element={<ReservationShow />} />

      {/* Extra routes (global add-ons) */}
      <Route path="/extras" element={<ExtraList />} />
      <Route path="/extras/create" element={<ExtraCreate />} />
      <Route path="/extras/edit/:id" element={<ExtraEdit />} />
      <Route path="/extras/show/:id" element={<ExtraShow />} />
      <Route path="/extras/:id/fees" element={<ExtraFees />} />

      {/* Insurance routes (global insurance options) */}
      <Route path="/insurances" element={<InsuranceList />} />
      <Route path="/insurances/create" element={<InsuranceCreate />} />
      <Route path="/insurances/edit/:id" element={<InsuranceEdit />} />
      <Route path="/insurances/show/:id" element={<InsuranceShow />} />
      <Route path="/insurances/:id/fees" element={<InsuranceFees />} />

      {/* Season routes (recurring yearly date periods) */}
      <Route path="/seasons" element={<SeasonList />} />
      <Route path="/seasons/create" element={<SeasonCreate />} />
      <Route path="/seasons/edit/:id" element={<SeasonEdit />} />
      <Route path="/seasons/show/:id" element={<SeasonShow />} />
      <Route path="/seasons/:id/fees" element={<SeasonFees />} />

      {/* Calendar route */}
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/daily-rates" element={<DailyRates />} />
      <Route path={ROUTES.SETUP} element={<SetupPage />} />
      <Route path={ROUTES.OFFICE_HOURS} element={<OfficeHoursPolicies />} />
      <Route path={ROUTES.OFFICE_HOURS_EDIT} element={<OpeningHoursEditor />} />
      <Route path={ROUTES.OPENING_HOURS_V2_STATION} element={<StationOpeningHoursPage />} />
      <Route path={ROUTES.EXTRA_DRIVERS} element={<ExtraDriverPolicies />} />
      <Route path={ROUTES.DRIVER_AGE} element={<DriverAgePolicies />} />
      {/* The Age limits page became Driver age on 2026-11-03. */}
      <Route path="/policies/age-limits" element={<Navigate to={ROUTES.DRIVER_AGE} replace />} />
      <Route path={ROUTES.ROAD_ASSISTANCE} element={<RoadAssistancePolicies />} />
      <Route path={ROUTES.KM_POLICIES} element={<KmPolicies />} />
      <Route path={ROUTES.DISCOUNTS} element={<DiscountPolicies />} />
      <Route path={ROUTES.RENTAL_DURATION} element={<RentalDurationPolicies />} />
      <Route path="/other-policies/*" element={<LegacyPolicyRedirect />} />
    </Route>

    {/* The Rental profile needs a Rental too, with its own refusal wording */}
    <Route element={<RentalRouteGuard impersonationMessage={RENTAL_PROFILE_SCOPE_MESSAGE} />}>
      <Route path={ROUTES.RENTAL_PROFILE} element={<RentalProfilePage />} />
    </Route>

    {/* Platform screens (platform-admin only, in Global view) */}
    <Route element={<PlatformRouteGuard screenName="Rentals" />}>
      <Route path="/rentals" element={<RentalList />} />
      <Route path="/rentals/show/:id" element={<RentalShow />} />
    </Route>
    <Route element={<PlatformRouteGuard screenName="Security Events" />}>
      <Route path={ROUTES.SECURITY_EVENTS} element={<SecurityEventList />} />
    </Route>
    <Route element={<PlatformRouteGuard screenName="All users" />}>
      <Route path={ROUTES.ADMIN_USERS} element={<UserList scope="platform" />} />
      <Route path={ROUTES.ADMIN_USERS_SHOW} element={<UserShow scope="platform" />} />
    </Route>

    {/* Global vehicle model routes (platform-admin only, in Global view) */}
    <Route element={<GlobalVehicleModelsRouteGuard />}>
      <Route path={ROUTES.GLOBAL_VEHICLE_MODELS} element={<GlobalVehicleModelList />} />
      <Route path={ROUTES.GLOBAL_VEHICLE_MODELS_CREATE} element={<GlobalVehicleModelCreate />} />
      <Route path={ROUTES.GLOBAL_VEHICLE_MODELS_EDIT} element={<GlobalVehicleModelEdit />} />
      <Route path={ROUTES.GLOBAL_VEHICLE_MODELS_SHOW} element={<GlobalVehicleModelShow />} />
    </Route>

    {/* 404 Not Found */}
    <Route path="/*" element={<NotFound />} />
  </>
);
